import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewWillLeave } from '@ionic/angular';
import { DocumentFileDto, DocumentFileType, FileDto, PropertyAcquisitionDocument, StatusPreenchimento } from '@usucampeao/lib-reurb-simplificado';
import { IMAGES_PATH } from '@usucampeao/utils-frontend';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { DocumentoService } from '../../../services';
import { ToastService } from '../../../shared';
import { Arquivo, UploadFotoComponent } from '../upload-foto.component';

export type UploadFotoDocumentoV2 = {
  tipo: DocumentFileType;
  nome?: string;
};

@Component({
  selector: 'usucampeao-upload-foto-documento-v2',
  templateUrl: './upload-foto-documento-v2.component.html',
  styleUrls: ['./upload-foto-documento-v2.component.scss']
})
export class UploadFotoDocumentoV2Component extends UploadFotoComponent implements OnInit, ViewWillLeave, OnDestroy {
  @Input() set documento(documento: DocumentFileDto) {
    this._documento = documento;
    if (this._documento) {
      this.documentoTipo = this.documentoTipo ?? this._documento.type;
      this.documentoNome = this.documentoNome ?? this._documento.name;
      this.carregarArquivosDocumento();
    } else {
      this.arquivosDocumento = [];
    }
  }
  @Input() titulo: string;
  @Input() statusPreenchimento: StatusPreenchimento;
  @Input() listaDeDocumentos: DocumentFileType[];
  @Input() documentoTipo: DocumentFileType;
  @Input() readonly = false;
  @Input() accordionEstaAberto = false;

  @Output() aoSelecionarDocumento = new EventEmitter<UploadFotoDocumentoV2>();

  private readonly ngUnsubscribe$ = new Subject<void>();

  private _documento: DocumentFileDto;
  public documentoNome: string;
  public form: FormGroup;
  public arquivosDocumento: Arquivo[] = [];

  constructor(
    private readonly documentoService: DocumentoService,
    @Inject(IMAGES_PATH) protected imagesPath: string,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    protected readonly toastService: ToastService,
  ) {
    super(toastService);
    this.form = this.formBuilder.group({
      documento: [null, Validators.required],
      nome: [null],
    });
  }

  ngOnInit(): void {
    this.form.get('documento')
      .valueChanges
      .pipe(
        filter(documento => !!documento),
        distinctUntilChanged(),
        tap(documento => {
          const documentoNomeCtrl = this.form.get('nome');
          documentoNomeCtrl.setValue(null);
          if (documento === PropertyAcquisitionDocument.OTHERS) {
            documentoNomeCtrl.setValidators([Validators.required]);
          } else {
            documentoNomeCtrl.clearValidators();
          }
          documentoNomeCtrl.updateValueAndValidity();
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  ionViewWillLeave(): void {
    this.ngUnsubscribe$.next();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.complete();
  }

  public get imagemDefault(): string {
    return `${this.imagesPath}/placeholder.png`;
  }

  public get imagemCarregando(): string {
    return `${this.imagesPath}/defaultImage.svg`;
  }

  public get possuiImagem(): boolean {
    return (this.imagensBase64 as Arquivo[]).length > 0;
  }

  public get arquivoPDF(): boolean {
    return (this.imagensBase64 as Arquivo[])[this.indiceImagemSelecionada]?.arquivoPdf;
  }

  public get mostrarBotaoVoltar(): boolean {
    return this.indiceImagemSelecionada > 0 && !this.readonly;
  }

  public get mostrarBotaoAvancar(): boolean {
    return (this.indiceImagemSelecionada + 1 < (this.imagensBase64 as Arquivo[]).length) && !this.readonly;
  }

  public gerarMensagemErro(controlName = 'documento'): string {
    const control = this.form.get(controlName);
    return control.hasError('required') && control.touched ? 'Campo obrigatório' : '';
  }

  public get documentoOutroSelecionado(): boolean {
    return this.form.get('documento').value === PropertyAcquisitionDocument.OTHERS;
  }

  public carregarArquivosDocumento(): void {
    this.documentoService.buscarArquivosDocumento(this._documento.id)
      .pipe(
        filter(arquivos => !!arquivos?.length),
        tap(arquivos => this.configurarArquivosDocumento(arquivos)),
      )
      .subscribe();
  }

  public configurarArquivosDocumento(arquivos: FileDto[]): void {
    if (!arquivos) {
      this.arquivosDocumento = [];
    } else {
      this.arquivosDocumento = arquivos.map(arquivo => ({
        id: arquivo.id,
        arquivoPdf: arquivo.mimeType.includes('pdf'),
        base64: arquivo.url_admin || `/documents/${this._documento.id}/files/${arquivo.id}/download`
      }));
    }
  }

  public abrirCamera(): void {
    this.form.markAllAsTouched();
    if (
      !this.readonly &&
      this.listaDeDocumentos?.length &&
      this.form.invalid
    ) {
      return;
    }
    this.resetar();
    this.uploadEstaDesabilitado = false;
    const { documento, nome } = this.form.value;
    this.aoSelecionarDocumento.emit({ tipo: documento, nome });
    super.selecionarImagem();
  }

  public irParaUploadDeDocumento(): void {
    this.router.navigate(
      ['../envio-documentos/upload'],
      {
        queryParams: {
          tipo: this._documento.type,
          documentoId: this._documento.id,
          nome: this.form.get('nome').value,
        },
        relativeTo: this.route,
      }
    );
  }

  public alterarAberturaAccordion(estaAberto: boolean): void {
    this.accordionEstaAberto = estaAberto;
  }
}
